const m = require('mithril');
const BasePlugin = require('./basePlugin');
const { LOCATION_CLASSES, processGeoJSON } = require('../mapUtils');
const pointSubscriberClass = require('../../components/pointSubscriberClass');
const colorscales = require('../../../../../lib/colorScales');

const QuickviewPopup = require('./quickviewPopup');
const edit = require('../../layout/components/edit');
class ZoneLayerPlugin extends BasePlugin {
  constructor(ctrl) {
    super(ctrl);
    
    // One shared subscriber for every zone polygon
    const zonePointSubscriber = new pointSubscriberClass();
    const deviationSubscribers = new Map();  

    const handleFeatureAdditions = async (features) => {
      if (!Array.isArray(features)) return;
      const allowedViews = ['HVAC Zones', 'Pharmacy', 'All Equipment', 'Lighting'];
      const isAllowedView = allowedViews.includes(ctrl.mapSelectedView());
      const includeArr = typeof ctrl.alwaysIncludeRids === 'function'
        ? (ctrl.alwaysIncludeRids() || [])
        : (ctrl.alwaysIncludeRids || []);
      const alwaysInclude = new Set(includeArr);
      for (const feature of features) {
        let context = ctrl.contexts.find(c => c.name === feature.context);
        let zoneRecord = feature.in ? feature.in : feature;
        
        if (!zoneRecord['@class'].includes("Zone")) continue;
        // If current view normally hides zones, allow-list specific zones
        if (!isAllowedView && !alwaysInclude.has(zoneRecord['@rid'])) continue;

        const locationId = zoneRecord['@rid'].toString();
        const href = `/location/${locationId}`;
        let polygon = processGeoJSON(zoneRecord, locationId);

        // Force zone geometry into the zones pane
        if (polygon && polygon.getLayers) {
          polygon.getLayers().forEach(l => {
            if (l && l.options) {
              l.options.pane = 'pane-zones';
              if (l.redraw) l.redraw();
            }
          });
        } else if (polygon && polygon.options) {
          polygon.options.pane = 'pane-zones';
        }

        polygon.UID = feature.UID;
        polygon['@class'] = zoneRecord['@class'];
        polygon['@rid'] = zoneRecord['@rid'];
        if (!polygon || polygon?.geojson?.features.length == 0) continue;

        let zoneStyle = {
          color: '#58ABE2',
          weight: 1.5,
          opacity: 1,
          dashArray: "5,5",
          fillColor: '#FFFFFF',
          fillOpacity: 0.3
        };

        // Emphasize focused zone or zones included in focus bounds
        const childRidArr = Array.isArray(ctrl.focusRidChildren)
          ? ctrl.focusRidChildren
          : (typeof ctrl.focusRidChildren === 'function' ? (ctrl.focusRidChildren() || []) : []);
        const focusSet = new Set([ctrl.focusRid, ...childRidArr].filter(Boolean));
        if (focusSet.has(zoneRecord['@rid'])) {
          // override style for focused zone
          Object.assign(zoneStyle, {
            weight: 4,
            color: '#FFFFFF',
            dashArray: "10,10",
          });
        }

        polygon.setStyle(zoneStyle);

        // ------------------------------------------------------------------
        //  ⬇️  NEW  — live‑colour the polygon via pointSubscriberClass
        // ------------------------------------------------------------------
        const deviationPointRid = findDeviationPointRid(zoneRecord);
        if (deviationPointRid) {
          /* 1️⃣  Build a tiny column object that the subscriber recognises   */
          const columnIn = { '@rid': deviationPointRid };
          Object.defineProperty(columnIn, 'value', {
            get()  { return this._v; },
            set(v) {
              this._v = v;
              const fill = colorscales.getDeviationColor(v);
              // Determine fill opacity: hide fill at zero, semi‑opaque otherwise
              const fOpacity = Math.abs(v) < 0.1 ? 0.4 : 0.8;
              polygon.setStyle({
                // re‑apply original stroke style so strokes remain visible
                color:    zoneStyle.color,
                weight:   zoneStyle.weight,
                opacity:  zoneStyle.opacity,
                dashArray:zoneStyle.dashArray,
                stroke: true,
                // fill
                fillColor:   fill,
                fillOpacity: fOpacity
              });
            },
            enumerable: true,
            configurable: true
          });

          /* 2️⃣  Minimal ctrl object passed to subscribe()                   */
          const ctrlObj = {
            column : { in: columnIn },
            options: { skipEditCheck: true, keepPollingOnModalOpen: true }
          };

          /* 3️⃣  Subscribe through ONE shared subscriber instance            */
          zonePointSubscriber.subscribe(ctrlObj, /* diagram */ null);
          deviationSubscribers.set(feature.UID, ctrlObj);
        }

        try {
          ctrl.zoneParentLayer.addLayer(polygon);
          ctrl.allLayers.push(polygon);
          // Prepare quick view popup to be bound only when not editing
          polygon._quickviewRecord = zoneRecord;
          polygon.UID = feature.UID;
          polygon.isPopupBound = false;
          bindOrUnbindZonePopup(polygon);
        } catch (e) {
          console.error('Error adding polygon:', polygon, e);
        }

      };
    };

    const handleFeatureRemovals = async (featureUids) => {
      featureUids.forEach(featureUid => {
        // Remove the polygon (if it exists) no matter what:
        const polygon = ctrl.allLayers.find(layer => layer.UID === featureUid && typeof layer.setStyle === 'function');
        if (polygon) {
          ctrl.map.removeLayer(polygon);
          let polygonIndex = ctrl.allLayers.indexOf(polygon);
          if (polygonIndex > -1) ctrl.allLayers.splice(polygonIndex, 1);
        }

        const ctrlObj = deviationSubscribers.get(featureUid);
        if (ctrlObj) {
          zonePointSubscriber.unsubscribe(ctrlObj);
          deviationSubscribers.delete(featureUid);
        }

      });
    };

    ctrl.eventBus.on('featuresToAdd', handleFeatureAdditions);
    ctrl.eventBus.on('featuresToRemove', handleFeatureRemovals);

    ctrl.eventBus.on('mithril:oncreate', () => {
      ctrl.zoneParentLayer = new L.FeatureGroup([], { pane: 'pane-zones' });
      ctrl.map.addLayer(ctrl.zoneParentLayer);
    });
    // Keep popup bindings in sync with edit mode and updates
    ctrl.eventBus.on('mithril:onupdate', () => updateAllZonePopupBindings());
    edit.on('toggle', () => updateAllZonePopupBindings());

    function bindOrUnbindZonePopup(layer) {
      if (!layer || !layer.bindPopup || !layer.unbindPopup) return;
      if (edit.vm.editing()) {
        if (layer.isPopupBound) {
          try { layer.unbindPopup(); } catch (e) {}
          layer.isPopupBound = false;
        }
        return;
      }
      if (!layer.isPopupBound) {
        try {
          const popup = QuickviewPopup(layer, layer._quickviewRecord, ctrl.map);
          popup.UID = layer.UID;
          layer.bindPopup(popup, { autoClose: false, closeOnClick: false, interactive: false });
          layer.isPopupBound = true;
        } catch (e) {}
      }
    }

    function updateAllZonePopupBindings() {
      try {
        ctrl.zoneParentLayer?.getLayers().forEach(bindOrUnbindZonePopup);
      } catch (e) {}
    }
  }
}

// ---------------------------------------------------------------------------
// Helper – walk the out_Contains graph looking for Default → Deviation column
// Returns the @rid string of the PRESENT_VALUE point, or null.
// ---------------------------------------------------------------------------
function findDeviationPointRid(zoneRecord) {
  const contains = zoneRecord.out_Contains || [];
  for (const ds of contains) {
    const dataset = ds.in;
    if (dataset?.name !== 'Default') continue;
    for (const col of dataset.out_Column || []) {
      if (col.name === 'Deviation' && col.in?.['@rid']) {
        return col.in['@rid'].toString();
      }
    }
  }
  return null;
}

module.exports = ZoneLayerPlugin;
